import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import SearchBar from '@/components/SearchBar';
import PropertyCard from '@/components/PropertyCard';
import MapView from '@/components/MapView';
import AdBanner from '@/components/AdBanner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Map, LayoutGrid, ChevronLeft, ChevronRight, ShieldCheck, ArrowUpDown, MapPin } from 'lucide-react';
import { useTracker } from '@/hooks/useTracker';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SearchPage() {
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [showMap, setShowMap] = useState(false);
  // Visible map bounds — only set when the user has actively moved/zoomed the map.
  // When non-null, the query filters by this bbox AND the property list updates
  // alongside it. Cleared when Search-by-Map is turned off.
  const [mapBounds, setMapBounds] = useState(null);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [verifiedPropertyOnly, setVerifiedPropertyOnly] = useState(false);
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [resolvedState, setResolvedState] = useState(searchParams.get('state') || '');
  const [resolvedCity, setResolvedCity] = useState(searchParams.get('city') || '');
  const { track } = useTracker(token);

  // Reverse-lookup state from city when only city is provided (e.g. /search?city=Caracas from Popular Cities)
  useEffect(() => {
    const urlState = searchParams.get('state') || '';
    const urlCity = searchParams.get('city') || '';
    setResolvedState(urlState);
    setResolvedCity(urlCity);
    if (urlCity && !urlState) {
      axios.get(`${API}/locations/lookup`, { params: { city: urlCity } })
        .then(r => {
          if (r.data?.found) {
            setResolvedState(r.data.state);
            setResolvedCity(r.data.city_name);
          }
        })
        .catch(() => {});
    }
  }, [searchParams]);

  const initialValues = {
    state: resolvedState,
    city: resolvedCity,
    property_type: searchParams.get('property_type') || '',
    operation_type: searchParams.get('operation_type') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    bedrooms: searchParams.get('bedrooms') || '',
    bathrooms: searchParams.get('bathrooms') || '',
    condition_status: searchParams.get('condition_status') || '',
    condition_state: searchParams.get('condition_state') || '',
    floor_level: searchParams.get('floor_level') || '',
    features: searchParams.get('features') || '',
    posted_within: searchParams.get('posted_within') || 'any',
  };

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    // Read state/city DIRECTLY from searchParams to avoid a stale-state race:
    // `resolvedState` is updated by a sibling effect that depends on the same
    // `searchParams` — so reading it here would be one render behind, producing
    // the user-visible "one step lag" bug.
    const urlState = searchParams.get('state') || '';
    const urlCity = searchParams.get('city') || '';
    if (urlState) params.set('state', urlState);
    if (urlCity) params.set('city', urlCity);
    if (initialValues.property_type && initialValues.property_type !== 'all') params.set('property_type', initialValues.property_type);
    if (initialValues.operation_type && initialValues.operation_type !== 'all') params.set('operation_type', initialValues.operation_type);
    if (initialValues.min_price) params.set('min_price', initialValues.min_price);
    if (initialValues.max_price) params.set('max_price', initialValues.max_price);
    if (initialValues.bedrooms) params.set('bedrooms', initialValues.bedrooms);
    if (initialValues.bathrooms) params.set('bathrooms', initialValues.bathrooms);
    if (initialValues.condition_status) params.set('condition_status', initialValues.condition_status);
    if (initialValues.condition_state) params.set('condition_state', initialValues.condition_state);
    if (initialValues.floor_level) params.set('floor_level', initialValues.floor_level);
    if (initialValues.features) params.set('features', initialValues.features);
    if (initialValues.posted_within && initialValues.posted_within !== 'any') params.set('posted_within', initialValues.posted_within);
    if (verifiedOnly) params.set('verified_only', 'true');
    if (verifiedPropertyOnly) params.set('verified_property_only', 'true');
    if (sortBy) { params.set('sort_by', sortBy); params.set('sort_order', sortOrder); }
    // Map-bounds filter — applied only when Search-by-Map is active AND bounds known.
    if (showMap && mapBounds?.bbox) {
      params.set('bbox', mapBounds.bbox);
    }
    params.set('page', page);
    params.set('limit', showMap && mapBounds ? '60' : '12');  // give the map more pins when active

    axios.get(`${API}/properties?${params.toString()}`)
      .then(res => {
        setProperties(res.data.properties);
        setTotal(res.data.total);
        setPages(res.data.pages);
        track('search', { search_query: params.toString(), metadata: { results: res.data.total, map_bbox: !!(showMap && mapBounds) } });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [searchParams, page, verifiedOnly, verifiedPropertyOnly, sortBy, sortOrder, showMap, mapBounds]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="search-page">
      <div className="mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900">{t('search.title')}</h1>
        <p className="text-slate-500 text-sm mt-1">{t('search.showing')} {total} {t('search.properties')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Filters Sidebar — sticky column. Filter card scrolls INTERNALLY,
            ad is anchored at the bottom of the sidebar and always visible. */}
        <div className="lg:col-span-1">
          <div
            className="search-sidebar-sticky flex flex-col gap-4"
            data-testid="search-sidebar-container"
          >
            <div
              className="bg-white rounded-xl border border-border p-4 overflow-y-auto flex-1 min-h-0"
              data-testid="search-filters"
            >
              <h3 className="font-heading font-semibold text-sm mb-3 text-slate-700">{t('search.filters')}</h3>
              <SearchBar key={`${resolvedState}|${resolvedCity}`} initialValues={initialValues} autoApply />
              <div className="mt-4 pt-3 border-t border-border space-y-2">
                <button
                  onClick={() => setVerifiedOnly(!verifiedOnly)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${verifiedOnly ? 'bg-green-50 border-2 border-green-400 text-green-700' : 'bg-slate-50 border-2 border-transparent text-slate-500 hover:border-slate-200'}`}
                  data-testid="verified-filter-toggle"
                >
                  <ShieldCheck className={`w-4.5 h-4.5 ${verifiedOnly ? 'text-green-600' : 'text-slate-400'}`} />
                  <span>{language === 'es' ? 'Solo Agentes Verificados' : 'Verified Agents Only'}</span>
                  {verifiedOnly && <span className="ml-auto w-2 h-2 rounded-full bg-green-500" />}
                </button>
                <button
                  onClick={() => setVerifiedPropertyOnly(!verifiedPropertyOnly)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${verifiedPropertyOnly ? 'bg-emerald-50 border-2 border-emerald-400 text-emerald-700' : 'bg-slate-50 border-2 border-transparent text-slate-500 hover:border-slate-200'}`}
                  data-testid="verified-property-filter-toggle"
                >
                  <ShieldCheck className={`w-4.5 h-4.5 ${verifiedPropertyOnly ? 'text-emerald-600' : 'text-slate-400'}`} />
                  <span>{language === 'es' ? 'Solo Propiedades Verificadas' : 'Verified Properties Only'}</span>
                  {verifiedPropertyOnly && <span className="ml-auto w-2 h-2 rounded-full bg-emerald-500" />}
                </button>
              </div>
            </div>
            {/* Ad fixed at the bottom of the sidebar — never overlaps filters. */}
            <div className="shrink-0">
              <AdBanner position="search_sidebar" />
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="lg:col-span-3">
          {/* Sort & Toggle */}
          <div className="flex items-center justify-between mb-4 gap-3">
            <span className="text-sm text-slate-500 flex-shrink-0">
              {t('search.showing')} {properties.length} {t('search.of')} {total} {t('search.properties')}
            </span>
            <div className="flex items-center gap-2">
              <Select value={sortBy ? `${sortBy}_${sortOrder}` : 'newest'} onValueChange={v => {
                if (v === 'newest') { setSortBy(''); setSortOrder('desc'); }
                else { const [f, o] = v.split('_'); setSortBy(f); setSortOrder(o); }
              }}>
                <SelectTrigger className="w-44 h-8 text-xs" data-testid="sort-select">
                  <ArrowUpDown className="w-3 h-3 mr-1" /><SelectValue placeholder={language === 'es' ? 'Ordenar' : 'Sort by'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">{language === 'es' ? 'Mas Reciente' : 'Newest'}</SelectItem>
                  <SelectItem value="price_asc">{language === 'es' ? 'Precio: Menor a Mayor' : 'Price: Low to High'}</SelectItem>
                  <SelectItem value="price_desc">{language === 'es' ? 'Precio: Mayor a Menor' : 'Price: High to Low'}</SelectItem>
                  <SelectItem value="area_desc">{language === 'es' ? 'Area (m2): Mayor' : 'Area (m2): Largest'}</SelectItem>
                  <SelectItem value="area_asc">{language === 'es' ? 'Area (m2): Menor' : 'Area (m2): Smallest'}</SelectItem>
                  <SelectItem value="land_size_desc">{language === 'es' ? 'Terreno: Mayor' : 'Land: Largest'}</SelectItem>
                  <SelectItem value="land_size_asc">{language === 'es' ? 'Terreno: Menor' : 'Land: Smallest'}</SelectItem>
                </SelectContent>
              </Select>
              <Button
                onClick={() => {
                  const next = !showMap;
                  setShowMap(next);
                  if (!next) setMapBounds(null);  // clear bbox when turning off
                  if (next) track('map_interaction', { metadata: { action: 'open_map_search' } });
                }}
                size="sm"
                className={`gap-1.5 ${
                  showMap
                    ? 'bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]'
                    : 'bg-[#0B192C] hover:bg-[#0B192C]/90 text-white'
                }`}
                data-testid="toggle-map-btn"
              >
                {showMap ? <LayoutGrid className="w-4 h-4" /> : <MapPin className="w-4 h-4" />}
                {showMap
                  ? (language === 'es' ? 'Cerrar Mapa' : 'Close Map')
                  : (language === 'es' ? 'Buscar en el Mapa' : 'Search by Map')}
              </Button>
            </div>
          </div>

          {/* Map View — Search by Map */}
          {showMap && (
            <div className="mb-6 animate-fade-in" data-testid="search-map-wrapper">
              <div className="flex items-start gap-2 mb-2 text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                <MapPin className="w-3.5 h-3.5 text-[#D4AF37] mt-0.5 shrink-0" />
                <span data-testid="map-hint">
                  {language === 'es'
                    ? 'Mueve y haz zoom en el mapa para filtrar propiedades en el área visible. La lista se actualiza automáticamente.'
                    : 'Pan and zoom the map to filter properties to the visible area. The list updates automatically.'}
                </span>
              </div>
              <div className="h-[480px] rounded-xl overflow-hidden border-2 border-[#D4AF37]/40 shadow-lg" data-testid="search-map">
                <MapView
                  properties={properties}
                  onBoundsChange={(b) => { setMapBounds(b); setPage(1); }}
                  fitToProperties={mapBounds === null}
                  center={mapBounds ? undefined : (resolvedState ? undefined : [8.0, -66.0])}
                  zoom={resolvedState ? 8 : 6}
                />
              </div>
            </div>
          )}

          {/* Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="rounded-xl overflow-hidden border border-border/50">
                  <Skeleton className="aspect-[4/3]" />
                  <div className="p-4 space-y-2"><Skeleton className="h-5 w-3/4" /><Skeleton className="h-4 w-1/2" /></div>
                </div>
              ))}
            </div>
          ) : properties.length === 0 ? (
            <div className="text-center py-16" data-testid="no-results">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <LayoutGrid className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-slate-500">{t('search.noResults')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {properties.map((prop, i) => (
                <div key={prop.id} className="animate-fade-in" style={{ animationDelay: `${i * 0.05}s` }}>
                  <PropertyCard property={prop} index={i} />
                </div>
              ))}
            </div>
          )}

          {/* In-feed ad between results */}
          {!loading && properties.length > 3 && (
            <div className="my-6" data-testid="ad-banner-search_between">
              <AdBanner position="search_between" />
            </div>
          )}

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8" data-testid="pagination">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-slate-600 px-3">{page} / {pages}</span>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
